import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@radix-ui/react-accordion';
import type { ReactNode } from 'react';

import { Icon } from '~/components';
import { cn } from '~/lib/cn';
import type { VariantProps } from '~/lib/variants';
import { variants } from '~/lib/variants';

const accordionItemVariants = variants({
  base: 'flex flex-col gap-lg rounded-sm p-lg bg-level-02',
  variants: {
    level: {
      2: 'bg-level-03',
    },
  },
});

interface AccordionContentBoxProps {
  title: string;
  children: ReactNode;
  defaultOpen?: boolean;
  className?: string;
  actions?: ReactNode;
  onValueChange?: (value: string) => void;
  variant?: VariantProps<typeof accordionItemVariants>;
  contentClassName?: string;
}

export function AccordionContentBox({
  title,
  actions,
  children,
  defaultOpen = true,
  className = '',
  variant,
  onValueChange,
  contentClassName = '',
}: AccordionContentBoxProps) {
  const contentId = `accordion-content-${title.replace(/\s+/g, '-').toLowerCase()}`;
  const variantClasses = accordionItemVariants(variant);

  return (
    <Accordion
      type="single"
      defaultValue={defaultOpen ? contentId : undefined}
      collapsible
      onValueChange={onValueChange}
    >
      <AccordionItem
        value={contentId}
        className={cn(variantClasses, className)}
      >
        <div className="flex items-center justify-between">
          <AccordionTrigger>
            <div
              id={contentId}
              className="text-headline-sm font-semibold leading-tight"
            >
              {title}
            </div>
          </AccordionTrigger>

          <div className="flex items-center gap-md">
            {actions}
            <AccordionTrigger className="group">
              <Icon
                className="transition-transform group-data-[state=open]:rotate-180"
                type="chevron-down"
              />
            </AccordionTrigger>
          </div>
        </div>
        <AccordionContent className={contentClassName}>
          {children}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

AccordionContentBox.displayName = 'AccordionContentBox';
